import { PricePart, PriceSegment } from '@icc/shared/types/price/Prices';
import hash from 'object-hash';
import { core } from '@icc/shared';
import { Common } from '@icc/shared/helpers';
import { PriceFilter } from '@icc/shared/types/price/price-filter';

export function sumPriceFromSegments(
    prices: PriceSegment[] = [],
    priceParts: PricePart[] = [],
    filters: PriceFilter[],
    beforePromotions = false
) {
    let sum = 0;
    filters.forEach((filter) => {
        const price = priceFromSegments(prices, priceParts, filter, beforePromotions);
        if (price !== null && sum !== null) {
            sum += price;
        } else {
            sum = null;
        }
    });
    return sum;
}

/**
 * Funkcja zwracająca cenę z segmentów
 * @param  {array}  prices  Tablica segmentów cen
 * @param  {string} type    Typ cen
 * @param  {number} sashId  Id skrzydła
 * @param  {number} isashId Id pola
 * @param  {number} divId   Id słupka
 * @return {number}         cena
 */
export function priceFromSegments(
    prices: PriceSegment[] = [],
    priceParts: PricePart[] = [],
    {
        value,
        percent,
    }: PriceFilter,
    beforePromotions = false
) {
    const valueSegs = prices.filter(
        (seg) =>
            seg &&
            value &&
            seg.type === value.type &&
            (!value ||
                !value.data ||
                Object.keys(value.data).every(
                    (key) =>
                        seg.data &&
                        (core.deepFind(seg.data, key) == value.data[key] ||
                            (Common.isObject(core.deepFind(seg.data, key)) &&
                                Common.isObject(value.data[key]) &&
                                hash(core.deepFind(seg.data, key)) === hash(value.data[key])) ||
                            (Common.isArray(value.data[key]) &&
                                value.data[key]
                                    .map(String)
                                    .includes(String(core.deepFind(seg.data, key)))) ||
                            (Common.isArray(value.data[key]) &&
                                Common.isArray(core.deepFind(seg.data, key)) &&
                                value.data[key]
                                    .map(String)
                                    .some((el) =>
                                        core.deepFind(seg.data, key).map(String).includes(el)
                                    )))
                ))
    );
    const percentSegs = prices.filter(
        (seg) =>
            seg &&
            percent &&
            seg.type === percent.type &&
            (!percent.data ||
                Object.keys(percent.data).every(
                    (key) => seg.data && core.deepFind(seg.data, key) === percent.data[key]
                ))
    );
    const parts = priceParts.filter(
        (pp) =>
            ((valueSegs.length > 0 && valueSegs.map((seg) => seg.id).indexOf(pp.valueId) > -1) ||
                !value ||
                value.type === '*') &&
            ((percentSegs.length > 0 &&
                percentSegs.map((seg) => seg.id).indexOf(pp.percentId) > -1) ||
                !percent ||
                percent.type === '*' ||
                (percent.type === pp.percentType && percent.type === null))
    );
    return parts.reduce((prev, curr) => {
        if (beforePromotions) {
            if (prev === null || curr.priceBeforePromotions === null) {
                return null;
            }
            return prev + curr.priceBeforeDiscounts;
        }
        if (prev === null || curr.priceBeforeDiscounts === null) {
            return null;
        }
        return prev + curr.priceBeforeDiscounts;
    }, 0);
}

/**
 * Helper function to get the highest price value from filtered prices.
 * Returns null if any matching price has null value, otherwise returns the max value.
 */
export function getHighestPriceValue(
    prices: any[] | undefined,
    filterFn: (price: any) => boolean
): number | null {
    return (
        prices
            ?.filter(filterFn)
            .map((el) => el.value)
            .reduce((acc: number | null, cur: number | null) => {
                if (cur == null) {
                    return null;
                }
                return acc === null ? null : +cur > +acc ? +cur : +acc;
            }, 0) ?? null
    );
}

/**
 * Helper function to get the highest price from filtered prices.
 * Returns null if any matching price has null value, otherwise returns the price with max value.
 */
export function getHighestPrice(
    prices: any[] | undefined,
    filterFn: (price: any) => boolean
): any | null {
    return (
        prices?.filter(filterFn).reduce((acc: any | null, cur: any | null) => {
            if (cur == null) {
                return acc;
            }
            return !acc || +cur.value > +acc?.value ? cur : acc;
        }, null) ?? null
    );
}

/**
 * Helper function to check if a value matches a dimension range.
 * Supports both [min, max] and [min, max, step] formats.
 * @param value - The value to check
 * @param dimension - The dimension array [min, max] or [min, max, step]
 * @returns true if value is within range (and matches step if provided)
 */
function matchesDimensionRange(value: number, dimension: (number | null)[]): boolean {
    if (!dimension || dimension.length < 2) {
        return false;
    }

    const min = +dimension[0];
    const max = +dimension[1];
    const step = dimension.length > 2 ? +dimension[2] : null;

    // Check min/max bounds
    if (min != null && +value < +min) {
        return false;
    }
    if (max != null && +value > +max) {
        return false;
    }

    // Check step if provided
    if (step != null && min != null && +step > 0) {
        const remainder = (+value - +min) % +step;
        if (remainder !== 0) {
            return false;
        }
    }

    return true;
}

/**
 * Helper function to check if a feature option matches a dimension.
 * Dimension can be a single value [id] or a range.
 * @param featureId - The feature option ID to check
 * @param dimension - The dimension array
 * @returns true if feature matches
 */
function matchesFeatureOption(
    featureId: number | string,
    dimension: (number | string | null)[]
): boolean {
    if (!dimension || dimension.length === 0) {
        return false;
    }

    // Single value match (feature option)
    if (dimension.length === 1) {
        return String(dimension[0]) === String(featureId);
    }

    // Range match (for numeric features)
    return matchesDimensionRange(+featureId, dimension as number[]);
}

/**
 * Check if a raster entry matches all dimension requirements.
 * Iterates through the definition to handle dimensions in any order.
 */
export function matchesRasterEntry<T>(
    rasterEntry: { d: any[]; p: number | null },
    definition: { featureId: string; type?: string }[],
    config: T,
    getDimensionValue: (featureId: string, config: T) => number | string | null
): boolean {
    for (let i = 0; i < definition.length; i++) {
        const def = definition[i];
        const dimension = rasterEntry.d[i];
        const configValue = getDimensionValue(def.featureId, config);

        if (configValue == null) {
            // If we don't have a value for this dimension, skip matching
            // (unless the dimension is required)
            continue;
        }

        // Check if the dimension is a range (numeric property like width/height)
        if (def.type === 'range' || (dimension.length >= 2 && typeof dimension[0] === 'number')) {
            if (!matchesDimensionRange(+configValue, dimension)) {
                return false;
            }
        } else {
            // Feature option match (single value like MontageTypes)
            if (!matchesFeatureOption(configValue, dimension)) {
                return false;
            }
        }
    }
    return true;
}
